/**
 * Navigation service for Data Compare feature.
 * Handles opening Data Compare tabs from context menu actions.
 */

import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { useConnectionStore } from '../../../connections/store/connectionStore';
import type {
  DataCompareEndpoint,
  DataCompareTab,
  DataCompareKeyMapping,
  DataCompareValueMapping,
  DataCompareOptions,
} from '../../core/types/workspaceTypes';
import { isDataCompareTab } from '../../core/types/workspaceTypes';
import { safeConnectionAlias, safeConnectionServer } from '../../../../types/safeConnectionDisplay';

/**
 * Context for opening Data Compare from table explorer
 */
export type DataCompareTableContext = {
  connectionId: string;
  database: string;
  schema: string;
  tableName: string;
};

/**
 * Find an existing open Data Compare tab.
 * Returns the tab if found, undefined otherwise.
 */
function findExistingDataCompareTab(): DataCompareTab | undefined {
  const { tabs } = useWorkspaceStore.getState();
  return tabs.find((tab): tab is DataCompareTab => isDataCompareTab(tab));
}

/**
 * Create endpoint from table context
 */
function createEndpointFromContext(context: DataCompareTableContext): DataCompareEndpoint {
  return {
    kind: 'connection',
    connectionId: context.connectionId,
    database: context.database,
    schema: context.schema,
    table: context.tableName,
  };
}

/**
 * Get connection info for a Data Compare tab
 */
function getConnectionInfoForTab(connectionId: string) {
  const { connections, activeConnections } = useConnectionStore.getState();
  const connection = connections.find(c => c.id === connectionId);
  const activeConnection = activeConnections.find(c => c.id === connectionId);

  if (!connection || !activeConnection) return undefined;

  return {
    connectionId,
    connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
    serverHost: safeConnectionServer(activeConnection.connection),
    database: activeConnection.connection.database,
  };
}

/**
 * Open Data Compare with a table set as source.
 * If a Data Compare tab already exists, navigates to it and updates the source.
 * Otherwise, creates a new Data Compare tab.
 */
export function openDataCompareAsSource(context: DataCompareTableContext): void {
  const { addTab, setActiveTab, updateTabContent } = useWorkspaceStore.getState();
  const existingTab = findExistingDataCompareTab();
  const endpoint = createEndpointFromContext(context);

  if (existingTab) {
    // Update existing tab's source and navigate to it
    updateTabContent(existingTab.id, {
      source: endpoint,
      // Clear comparison results when source changes
      hasCompared: false,
      operationId: undefined,
      summary: undefined,
      needsRefresh: false,
      error: undefined,
      selectedRowKey: undefined,
      selectedRowKeys: undefined,
      isSampleResult: undefined,
      sampledRowCount: undefined,
    });
    setActiveTab(existingTab.id);
  } else {
    // Create new Data Compare tab with source pre-filled
    const connectionInfo = getConnectionInfoForTab(context.connectionId);

    addTab({
      title: 'Data Compare',
      type: 'data-compare',
      content: {
        source: endpoint,
      },
      connectionInfo,
    });
  }
}

/**
 * Open Data Compare with a table set as target.
 * If a Data Compare tab already exists, navigates to it and updates the target.
 * Otherwise, creates a new Data Compare tab.
 */
export function openDataCompareAsTarget(context: DataCompareTableContext): void {
  const { addTab, setActiveTab, updateTabContent } = useWorkspaceStore.getState();
  const existingTab = findExistingDataCompareTab();
  const endpoint = createEndpointFromContext(context);

  if (existingTab) {
    // Update existing tab's target and navigate to it
    updateTabContent(existingTab.id, {
      target: endpoint,
      // Clear comparison results when target changes
      hasCompared: false,
      operationId: undefined,
      summary: undefined,
      needsRefresh: false,
      error: undefined,
      selectedRowKey: undefined,
      selectedRowKeys: undefined,
      isSampleResult: undefined,
      sampledRowCount: undefined,
    });
    setActiveTab(existingTab.id);
  } else {
    // Create new Data Compare tab with target pre-filled
    const connectionInfo = getConnectionInfoForTab(context.connectionId);

    addTab({
      title: 'Data Compare',
      type: 'data-compare',
      content: {
        target: endpoint,
      },
      connectionInfo,
    });
  }
}

/**
 * Open a NEW Data Compare tab with both a source and target endpoint already
 * set, but WITHOUT pre-derived mappings — Data Compare derives the column
 * mapping itself (the columns share names after a migration). Used by the
 * cross-engine migration "Verify with Data Compare" hook to confirm the copied
 * rows match the source. Always a fresh tab so it never clobbers an open compare.
 */
export function openDataCompareSourceAndTarget(args: {
  source: DataCompareEndpoint;
  target: DataCompareEndpoint;
  title?: string;
}): void {
  const { addTab } = useWorkspaceStore.getState();
  const connectionInfo =
    args.target.kind === 'connection' ? getConnectionInfoForTab(args.target.connectionId) : undefined;
  addTab({
    title: args.title ?? 'Verify migration',
    type: 'data-compare',
    content: {
      source: args.source,
      target: args.target,
      hasCompared: false,
      isComparing: false,
      needsRefresh: false,
    },
    connectionInfo,
  });
}

/**
 * Open a NEW Data Compare tab with fully pre-derived endpoints + mappings.
 * Used by the Database Blueprint "Open in Data Compare" action. Always creates
 * a fresh tab (mappings are table-specific, so reusing an existing tab would
 * be wrong). When `autoCompare` is set, DataCompareTab runs the comparison once
 * on mount.
 */
export function openDataCompareWithEndpoints(args: {
  source: DataCompareEndpoint;
  target: DataCompareEndpoint;
  keyMappings: DataCompareKeyMapping[];
  valueMappings: DataCompareValueMapping[];
  options?: DataCompareOptions;
  autoCompare?: boolean;
  title?: string;
}): void {
  const { addTab } = useWorkspaceStore.getState();
  const connectionInfo =
    args.target.kind === 'connection'
      ? getConnectionInfoForTab(args.target.connectionId)
      : undefined;

  addTab({
    title: args.title ?? 'Data Compare',
    type: 'data-compare',
    content: {
      source: args.source,
      target: args.target,
      keyMappings: args.keyMappings,
      valueMappings: args.valueMappings,
      ...(args.options ? { options: args.options } : {}),
      ...(args.autoCompare ? { autoCompare: true } : {}),
      hasCompared: false,
      isComparing: false,
      needsRefresh: false,
    },
    connectionInfo,
  });
}
